'use client'

import type { Column } from '@tanstack/react-table'
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react'
import { Button } from '~/components/ui/button'
import { cn } from '~/lib/utils'

export type DataTableSorterProps<TData> = {
  column: Column<TData>
} & React.ComponentProps<typeof Button>

function SortIcon({ sorted }: { sorted: false | 'asc' | 'desc' }) {
  if (sorted === 'desc') {
    return <ArrowDown className={cn('text-primary', '!w-3', '!h-3')} />
  }
  if (sorted === 'asc') {
    return <ArrowUp className={cn('text-primary', '!w-3', '!h-3')} />
  }
  return (
    <ChevronsUpDown className={cn('text-muted-foreground', '!w-3', '!h-3')} />
  )
}

export function DataTableSorter<TData>({
  column,
  className,
  ...props
}: DataTableSorterProps<TData>) {
  const sorted = column.getIsSorted()
  const title =
    sorted === false
      ? `Sort by ${column.id}`
      : `Sort by ${column.id} as ${sorted === 'desc' ? 'descending' : 'ascending'}`

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => column.toggleSorting(undefined, true)}
      title={title}
      aria-label={title}
      {...props}
      className={cn('data-[state=open]:bg-accent', 'w-5 h-5', className)}
    >
      <SortIcon sorted={sorted} />
    </Button>
  )
}

DataTableSorter.displayName = 'DataTableSorter'
